import React from 'react';
import { useT, useL } from '../../lib/lessonI18n';

// Vocal folds seen from above: open (voiceless) or vibrating (voiced), with the resulting waveform.
export const VoicingDiagram = ({ voiced }) => {
  const T = useT();
  return (
    <svg viewBox="0 0 220 120" className="w-full h-full" data-testid={`voicing-diagram-${voiced ? 'voiced' : 'voiceless'}`} role="img" aria-label={voiced ? T('sonora', 'voiced') : T('sorda', 'voiceless')}>
      <text x="55" y="12" fontSize="7" textAnchor="middle" fill="#64748b" fontWeight="700">{T('GLOTTIDE (dall\'alto)', 'GLOTTIS (from above)')}</text>
      <ellipse cx="55" cy="62" rx="38" ry="44" fill="rgba(15,23,42,0.8)" stroke="rgba(148,163,184,0.5)" strokeWidth="1" />
      <g className={voiced ? 'ct-fold-l' : ''} style={{ transformOrigin: '55px 26px', transform: voiced ? 'none' : 'rotate(14deg)', transition: 'transform .5s ease' }}>
        <path d="M55,26 Q44,62 52,98" fill="none" stroke="#f9a8d4" strokeWidth="5" strokeLinecap="round" />
      </g>
      <g className={voiced ? 'ct-fold-r' : ''} style={{ transformOrigin: '55px 26px', transform: voiced ? 'none' : 'rotate(-14deg)', transition: 'transform .5s ease' }}>
        <path d="M55,26 Q66,62 58,98" fill="none" stroke="#f9a8d4" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="55" y="116" fontSize="7" textAnchor="middle" fill={voiced ? '#a3e635' : '#94a3b8'} fontWeight="800">{voiced ? T('pliche addotte · vibrano', 'folds adducted · vibrating') : T('pliche aperte · aria libera', 'folds open · free air')}</text>
      <text x="160" y="12" fontSize="7" textAnchor="middle" fill="#64748b" fontWeight="700">{T('ONDA', 'WAVE')}</text>
      <line x1="110" y1="62" x2="215" y2="62" stroke="rgba(148,163,184,0.25)" strokeWidth="0.6" />
      {voiced
        ? <path d="M110,62 q4,-22 8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0 t8,0" fill="none" stroke="#a3e635" strokeWidth="1.6" className="ct-wave" />
        : <path d="M110,62 l3,-3 l2,5 l3,-6 l2,7 l3,-4 l2,3 l3,-6 l2,6 l3,-3 l2,4 l3,-5 l2,5 l3,-4 l2,3 l3,-2 l2,4 l3,-5 l2,3 l3,-3 l2,5 l3,-4 l2,2 l3,-3 l2,4 l3,-5 l2,3 l3,-2 l2,3 l3,-2 l2,2 l3,-3 l2,4 l3,-3" fill="none" stroke="#94a3b8" strokeWidth="1" />}
      <text x="162" y="104" fontSize="7" textAnchor="middle" fill="#cbd5e1">{voiced ? T('periodica: tono (si sente il "buzz")', 'periodic: pitch (audible "buzz")') : T('solo rumore d\'aria (o silenzio)', 'only air noise (or silence)')}</text>
    </svg>
  );
};

const SCALE = [
  { k: { it: 'Occlusive sorde', en: 'Voiceless plosives' }, ex: 'p t k', son: false },
  { k: { it: 'Occlusive sonore', en: 'Voiced plosives' }, ex: 'b d ɡ', son: false },
  { k: { it: 'Affricate', en: 'Affricates' }, ex: 'tʃ dʒ', son: false },
  { k: { it: 'Fricative', en: 'Fricatives' }, ex: 'f v θ ð s z ʃ ʒ h', son: false },
  { k: { it: 'Nasali', en: 'Nasals' }, ex: 'm n ŋ', son: true },
  { k: { it: 'Liquide', en: 'Liquids' }, ex: 'l ɹ', son: true },
  { k: { it: 'Semivocali', en: 'Semivowels' }, ex: 'j w', son: true },
  { k: { it: 'Vocali', en: 'Vowels' }, ex: 'iː æ ɑː…', son: true },
];

// Sonority scale: obstruents (noise) → sonorants (resonance, always voiced in English).
export const SonorityScale = () => {
  const T = useT();
  const L = useL();
  return (
    <div data-testid="sonority-scale" className="overflow-x-auto">
      <div className="min-w-[420px]">
      <div className="flex items-end gap-1.5 h-44">
        {SCALE.map((s, i) => (
          <div key={L(s.k)} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`sonority-step-${i}`}>
            <span className={`font-mono text-[11px] font-bold mb-1 text-center leading-tight ${s.son ? 'text-lime-200' : 'text-cyan-200'}`}>{s.ex}</span>
            <div className={`w-full rounded-t-lg ct-grow ${s.son ? 'bg-gradient-to-t from-lime-500/30 to-lime-300/80' : 'bg-gradient-to-t from-cyan-500/20 to-cyan-300/60'}`}
              style={{ height: `${18 + i * 11}%`, animationDelay: `${i * 80}ms` }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-2">
        {SCALE.map((s) => <span key={L(s.k)} className="flex-1 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-400 text-center leading-tight">{L(s.k)}</span>)}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-bold uppercase tracking-widest">
        <span className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 text-cyan-200">{T('← Ostruenti · rumore · sorde o sonore', '← Obstruents · noise · voiced or voiceless')}</span>
        <span className="rounded-lg border border-lime-500/30 bg-lime-500/5 px-3 py-2 text-lime-200 text-right">{T('Sonoranti · risonanza · sempre sonore →', 'Sonorants · resonance · always voiced →')}</span>
      </div>
      </div>
    </div>
  );
};
